/**
 * Debug utility for conditional logging
 *
 * Logs are only shown when:
 * - In development mode (import.meta.env.DEV is true)
 * - Or VITE_DEBUG=true in production (for debugging deployed builds)
 *
 * In production builds, all debug logs are silenced by default.
 * IMPORTANT: Never log PII (userId, email, full payloads) even in dev.
 */

const isDev = import.meta.env.DEV;
const isDebugEnabled = isDev || import.meta.env.VITE_DEBUG === 'true';

/**
 * Debug logger that only logs in development or when debug mode is enabled
 */
export const debug = {
  log: (...args: any[]) => {
    if (isDebugEnabled) {
      console.log(...args);
    }
  },

  warn: (...args: any[]) => {
    if (isDebugEnabled) {
      console.warn(...args);
    }
  },

  error: (...args: any[]) => {
    // Always log errors, even in production
    console.error(...args);
  },

  info: (...args: any[]) => {
    if (isDebugEnabled) {
      console.info(...args);
    }
  },
};

/**
 * Create a namespaced debug logger
 * Example: const debug = createDebugLogger('LessonBuilder');
 *
 * IMPORTANT: Never pass PII (userId, email, full profile objects) to these loggers.
 * Only log non-sensitive metadata like counts, field presence, or status.
 */
export function createDebugLogger(namespace: string) {
  const logger = (...args: any[]) => {
    if (isDebugEnabled) {
      console.log(`[${namespace}]`, ...args);
    }
  };

  // Also expose as object methods for backward compatibility
  logger.log = (...args: any[]) => {
    if (isDebugEnabled) {
      console.log(`[${namespace}]`, ...args);
    }
  };

  logger.warn = (...args: any[]) => {
    if (isDebugEnabled) {
      console.warn(`[${namespace}]`, ...args);
    }
  };

  logger.error = (...args: any[]) => {
    // Always log errors
    console.error(`[${namespace}]`, ...args);
  };

  logger.info = (...args: any[]) => {
    if (isDebugEnabled) {
      console.info(`[${namespace}]`, ...args);
    }
  };

  return logger;
}
